import React, { useState, useRef, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import TopPlays from './components/TopPlays';
import KPIs from './components/KPIs';
import OpsFeed from './components/OpsFeed';
import AskAgentBlaze from './components/AskAgentBlaze';
import SettingsPage from './components/SettingsPage';
import ReviewModal from './components/ReviewModal';
import LoginPage from './components/LoginPage';
// Create contexts for theme, layout, and location
export const ThemeContext = createContext({
  isDarkMode: true,
  toggleTheme: () => {}
});
export const LayoutContext = createContext({
  isQuadrantLayout: true,
  toggleLayout: () => {}
});
export const LocationContext = createContext({
  currentLocation: 'Fresno',
  setLocation: (location: string) => {},
  locations: ['Fresno', 'Los Angeles', 'San Francisco', 'Sacramento']
});
// Create a context for the AskAgentBlaze ref
export const AgentBlazeRefContext = createContext<React.RefObject<HTMLDivElement> | null>(null);
// Custom hook to use theme context
export const useTheme = () => useContext(ThemeContext);
export const useLayout = () => useContext(LayoutContext);
export const useLocation = () => useContext(LocationContext);
export const useAgentBlazeRef = () => useContext(AgentBlazeRefContext);
export function App() {
  // State for theme, layout, and location
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isQuadrantLayout, setIsQuadrantLayout] = useState(true);
  const [currentLocation, setCurrentLocation] = useState('Fresno');
  const locations = ['Fresno', 'Los Angeles', 'San Francisco', 'Sacramento'];
  // State for Agent BLAZE mode
  const [isAutoMode, setIsAutoMode] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<any>(null);
  // Create a ref for the AskAgentBlaze section
  const agentBlazeRef = useRef<HTMLDivElement>(null);
  // Toggle functions
  const toggleTheme = () => setIsDarkMode(!isDarkMode);
  const toggleLayout = () => setIsQuadrantLayout(!isQuadrantLayout);
  const setLocation = (location: string) => setCurrentLocation(location);
  const toggleMode = () => setIsAutoMode(!isAutoMode);
  const handleActionClick = (action: any) => {
    setSelectedAction(action);
    setShowActionModal(true);
  };
  // Main dashboard component
  const Dashboard = () => {
    return <div className={`min-h-screen w-full ${isDarkMode ? 'bg-[#0d1117] text-white' : 'bg-[#f9fafb] text-black'}`} data-id="element-0">
        <Header data-id="element-1" />
        <div className="px-8 py-4" data-id="element-2">
          {/* Agent status bar */}
          <div className={`w-full p-3 mb-4 rounded-lg ${isDarkMode ? 'bg-[#161b22]' : 'bg-[#f0f5f3]'}`} data-id="element-3">
            <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-4">
              <div className="flex items-center" data-id="element-5">
                <span className="text-[#2196f3] mr-2" data-id="element-6">&#9673;</span>
                <span className="font-semibold" data-id="element-7">
                  Agent BLAZE prepped your day
                </span>
                <span className="ml-2 px-2 py-0.5 bg-[#2196f3] bg-opacity-20 text-[#2196f3] text-xs rounded-full" data-id="element-8">
                  {isAutoMode ? 'Auto' : 'Manual'}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-9">
                <div className="text-sm text-gray-400 flex items-center" data-id="element-10">
                  <span data-id="element-11">Last sweep run (06:30 06/15/2023)</span>
                </div>
                <div className="flex items-center space-x-3" data-id="element-12">
                  <div className="flex items-center" data-id="element-13">
                    <button onClick={toggleMode} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAutoMode ? 'bg-[#2196f3]' : 'bg-gray-600'}`} data-id="element-14">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAutoMode ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-15" />
                    </button>
                    <span className="ml-2 text-sm" data-id="element-16">
                      {isAutoMode ? 'Auto' : 'Manual'}
                    </span>
                  </div>
                  <button className="flex items-center px-3 py-1 rounded-md bg-[#2196f3] text-white hover:bg-[#1976d2]" disabled={isAutoMode} data-id="element-17">
                    Run Sweep{' '}
                    <span className="ml-1 text-xs" data-id="element-18">(XL - 15 credits)</span>
                  </button>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-400 mt-1" data-id="element-19">
              Queued <span className="text-white" data-id="element-20">3 Top Plays</span> and{' '}
              <span className="text-white" data-id="element-21">2 Watchouts</span> for{' '}
              {currentLocation}
            </p>
          </div>
          {/* Main content grid */}
          <div className={`grid gap-4 ${isQuadrantLayout ? 'lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-1'}`} data-id="element-22">
            <TopPlays data-id="element-23" />
            <KPIs data-id="element-24" />
            {isQuadrantLayout ? <>
                <OpsFeed data-id="element-25" />
                <div ref={agentBlazeRef} data-id="element-26">
                  <AskAgentBlaze data-id="element-27" />
                </div>
              </> : <div className="lg:grid lg:grid-cols-2 gap-4" data-id="element-28">
                <OpsFeed data-id="element-29" />
                <div ref={agentBlazeRef} data-id="element-30">
                  <AskAgentBlaze data-id="element-31" />
                </div>
              </div>}
          </div>
        </div>
        {/* Action Modal */}
        {showActionModal && selectedAction && <ReviewModal action={selectedAction} onClose={() => setShowActionModal(false)} data-id="element-32" />}
      </div>;
  };
  return <ThemeContext.Provider value={{
    isDarkMode,
    toggleTheme
  }} data-id="element-33">
      <LayoutContext.Provider value={{
      isQuadrantLayout,
      toggleLayout
    }} data-id="element-34">
        <LocationContext.Provider value={{
        currentLocation,
        setLocation,
        locations
      }} data-id="element-35">
          <AgentBlazeRefContext.Provider value={agentBlazeRef} data-id="element-36">
            <Router data-id="element-37">
              <Routes data-id="element-38">
                <Route path="/" element={<Dashboard data-id="element-40" />} data-id="element-39" />
                <Route path="/settings" element={<SettingsPage data-id="element-42" />} data-id="element-41" />
                <Route path="/login" element={<LoginPage data-id="element-44" />} data-id="element-43" />
              </Routes>
            </Router>
          </AgentBlazeRefContext.Provider>
        </LocationContext.Provider>
      </LayoutContext.Provider>
    </ThemeContext.Provider>;
}